import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import type { PatientIndexRow, SessionRow } from '@/lib/db/schema';
import { formatDate, formatTime, toMyanmarDigits } from '@/lib/myanmar/digits';

export function TokenSlip({ label, patient, session, clinicName, autoPrint = false, onClose }: { label: string; patient: PatientIndexRow; session: SessionRow & { doctor_name_en: string | null; doctor_name_my: string | null }; clinicName: string; autoPrint?: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  // Auto-print: send to the printer as soon as the slip renders, then close.
  useEffect(() => {
    if (!autoPrint) return;
    const done = () => { window.removeEventListener('afterprint', done); onClose(); };
    window.addEventListener('afterprint', done);
    const id = setTimeout(() => window.print(), 150);
    return () => { clearTimeout(id); window.removeEventListener('afterprint', done); };
  }, [autoPrint, onClose]);
  const my = i18n.language === 'my';
  const now = new Date();
  return (
    <div className="grid gap-4">
      <div className="print-slip mx-auto w-full max-w-xs rounded-lg border border-dashed border-rule bg-white p-4 text-center text-ink">
        <div className="text-sm font-semibold">{clinicName}</div>
        <div className="mt-2 text-xs uppercase tracking-widest text-muted">{t('queue.token')}</div>
        <div className="text-6xl font-bold leading-none">{my ? toMyanmarDigits(label) : label}</div>
        <div className="mt-3 text-sm">{my ? (session.doctor_name_my ?? session.doctor_name_en) : (session.doctor_name_en ?? session.doctor_name_my)}{session.room ? ` · ${t('queue.room')} ${session.room}` : ''}</div>
        <div className="text-sm">{my ? (patient.name_my ?? patient.name_en) : (patient.name_en ?? patient.name_my)} · {patient.patient_no}</div>
        <div className="mt-2 text-xs text-muted">{formatDate(now, my ? 'my' : 'en')} {formatTime(now, my ? 'my' : 'en')}</div>
        <div className="mt-2 text-xs">{t('queue.pleaseWait')}</div>
      </div>
      <div className="no-print flex justify-end gap-2">
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="primary" onClick={() => window.print()}>{t('common.print')}</Button>
      </div>
    </div>
  );
}
